/**
 * Gallery filter by image alt.
 * Only runs when this product has at least one image alt starting with #Color_.
 * Uses DOM order (not Shopify media.position) so deleted images with position
 * gaps do not leave untagged photos visible.
 */
const filterState = {
  active: false,
  variantId: null,
  applying: false,
};

function parseGalleryConfig(gallery) {
  const script = gallery.querySelector('script[data-variant-media-filter]');
  if (!script) return { variants: [] };
  try {
    return JSON.parse(script.textContent || '{}');
  } catch (_) {
    return { variants: [] };
  }
}

function colorKey(alt) {
  const first = String(alt || '')
    .trim()
    .split(/\s+/)[0] || '';
  return /^#color_/i.test(first) ? first.toLowerCase() : '';
}

function showAllMedia(gallery) {
  gallery.removeAttribute('data-variant-filter-active');
  gallery.setAttribute('filtered-indexes', '[]');
  gallery.querySelectorAll('.product-gallery__media').forEach((slide) => {
    slide.hidden = false;
  });
  gallery.querySelectorAll('.product-gallery__thumbnail, page-dots .tap-area').forEach((item) => {
    item.hidden = false;
  });
}

function applyVariantMediaFilter(gallery, incomingVariant) {
  if (!gallery || !incomingVariant) return;

  const config = parseGalleryConfig(gallery);
  const variantId = Number(incomingVariant.id);
  const fromConfig = (config.variants || []).find((item) => Number(item.id) === variantId) || {};
  const slides = Array.from(gallery.querySelectorAll('.product-gallery__media'));
  const featuredMediaId = fromConfig.featured_media_id || (incomingVariant.featured_media && incomingVariant.featured_media.id);
  const featuredSlide = slides.find(
    (slide) => featuredMediaId && String(slide.getAttribute('data-media-id')) === String(featuredMediaId)
  );
  const variantAlt =
    fromConfig.featured_media_alt ||
    incomingVariant.featured_media_alt ||
    (featuredSlide && featuredSlide.getAttribute('data-media-alt')) ||
    '';
  const wantedKey = colorKey(variantAlt);
  const galleryHasColorAlt = slides.some((slide) => colorKey(slide.getAttribute('data-media-alt') || ''));

  if (!galleryHasColorAlt) {
    showAllMedia(gallery);
    filterState.active = false;
    filterState.variantId = variantId;
    return;
  }

  filterState.applying = true;
  const hiddenDomIndexes = [];

  slides.forEach((slide, index) => {
    const alt = slide.getAttribute('data-media-alt') || '';
    const mediaId = slide.getAttribute('data-media-id');
    const isFeatured = featuredMediaId && String(mediaId) === String(featuredMediaId);
    const key = colorKey(alt);
    const keep = isFeatured || (wantedKey !== '' && key === wantedKey);

    slide.hidden = !keep;
    if (!keep) hiddenDomIndexes.push(index);
  });

  gallery.querySelectorAll('.product-gallery__thumbnail').forEach((item, index) => {
    item.hidden = hiddenDomIndexes.includes(index);
  });
  gallery.querySelectorAll('page-dots .tap-area').forEach((item, index) => {
    item.hidden = hiddenDomIndexes.includes(index);
  });

  const oneBased = hiddenDomIndexes.map((index) => index + 1);
  gallery.setAttribute('filtered-indexes', JSON.stringify(oneBased));
  gallery.setAttribute('data-variant-filter-active', 'true');

  gallery.querySelectorAll('media-carousel').forEach((carousel) => {
    carousel.dispatchEvent(new CustomEvent('carousel:filter', { detail: { filteredIndexes: hiddenDomIndexes } }));
  });
  gallery.querySelectorAll('page-dots').forEach((dots) => {
    dots.dispatchEvent(new CustomEvent('control:filter', { detail: { filteredIndexes: hiddenDomIndexes } }));
  });

  let featuredPosition = slides.findIndex(
    (slide) => featuredMediaId && String(slide.getAttribute('data-media-id')) === String(featuredMediaId) && !slide.hidden
  );
  if (featuredPosition < 0) featuredPosition = slides.findIndex((slide) => !slide.hidden);
  if (featuredPosition >= 0) {
    gallery.querySelectorAll('media-carousel').forEach((carousel) => {
      if (typeof carousel.select === 'function') {
        carousel.select(featuredPosition, { animate: false });
      }
    });
  }

  filterState.active = true;
  filterState.variantId = variantId;
  filterState.applying = false;
  watchGallery(gallery);
}

function reapply() {
  if (!filterState.variantId) return;
  const gallery = document.querySelector('product-gallery[data-selected-variant-id], product-gallery');
  if (!gallery) return;
  applyVariantMediaFilter(gallery, { id: filterState.variantId });
}

function watchGallery(gallery) {
  if (gallery.dataset.filterWatched === 'true') return;
  gallery.dataset.filterWatched = 'true';
  const observer = new MutationObserver(() => {
    if (filterState.applying || !filterState.active) return;
    const current = gallery.getAttribute('filtered-indexes');
    if (current === '[]' || current === '' || current == null) reapply();
  });
  observer.observe(gallery, { attributes: true, attributeFilter: ['filtered-indexes'] });
}

function galleryForEvent(event) {
  const formId = event.detail && (event.detail.formId || (event.detail.form && event.detail.form.id));
  if (formId) {
    const named = document.querySelector(`product-gallery[form="${formId}"]`);
    if (named) return named;
  }
  return document.querySelector('product-gallery');
}

function bindVariantMediaFilter() {
  if (window.__variantMediaFilterBound) return;
  window.__variantMediaFilterBound = true;

  document.addEventListener(
    'variant:change',
    (event) => {
      const variant = event.detail && event.detail.variant;
      if (!variant) return;
      const gallery = galleryForEvent(event);
      if (!gallery) return;
      applyVariantMediaFilter(gallery, variant);
    },
    true
  );

  document.addEventListener(
    'product:rerender',
    () => {
      if (!filterState.active) return;
      setTimeout(reapply, 0);
      setTimeout(reapply, 80);
    },
    true
  );
}

bindVariantMediaFilter();

document.querySelectorAll('product-gallery').forEach((gallery) => {
  const config = parseGalleryConfig(gallery);
  const selectedId = config.selectedVariantId || gallery.getAttribute('data-selected-variant-id');
  if (selectedId) applyVariantMediaFilter(gallery, { id: selectedId });
});

window.applyVariantMediaFilter = applyVariantMediaFilter;
window.applyVariantMediaFilterToRoot = function (root) {
  if (!root || typeof root.querySelectorAll !== 'function') return;
  root.querySelectorAll('product-gallery').forEach((gallery) => {
    const config = parseGalleryConfig(gallery);
    const selectedId = config.selectedVariantId || gallery.getAttribute('data-selected-variant-id');
    if (selectedId) applyVariantMediaFilter(gallery, { id: selectedId });
  });
};
window.resetVariantMediaFilter = function () {
  filterState.active = false;
  filterState.variantId = null;
  document.querySelectorAll('product-gallery[data-variant-filter-active]').forEach((gallery) => {
    showAllMedia(gallery);
  });
};
